import { Injectable } from '@angular/core';
import {
  IOperazioneFilter,
  IResponseOperationMonitoring,
} from '../interfaces/monitoring';
import { IDropdownOptions, IGridColumns } from '@poste-click-connect/shared-ui';
import {
  FilterStoricoOperazioni,
  ResponseStoricoOperazioni,
} from '../interfaces/response-operation-history';
import {
  generalStateDropdownOption,
  operazioneDropdownOption,
} from '../constants/monitoring.const';
import {
  OperazioneCRM,
  OperazioneSAP,
  OperazioneDashboard,
} from '../interfaces/retrieve-interface-monitoring';
import {
  statusAccordoOptions,
  statusAnagCrmoOptions,
  statusAnagTypeS4HanaOptions,
  systemOptions,
  systemOptionsLocker,
  typeOptionsCrm,
  typeOptionsDashboard,
  typeOptionsS4hana,
} from '../pages/anagraphic/pages/interface-monitoring/interface-monitoring.utils';
import { ResponseInterfaceMonitoringSap } from '../interfaces/response-interface-monitoring-sap';
import { ResponseInterfaceMonitoringCrm } from '../interfaces/response-interface-monitoring-crm';
import { ResponseInterfaceMonitoringDashboard } from '../interfaces/response-interface-monitoring-dashboard';
import {
  OperazioneStoricoCrm,
  ResponseInterfaceHistoryCrm,
} from '../interfaces/response-interface-history-crm';
import {
  OperazioneSAP as OperazioneStoricoSap,
  IResponseStoricoSap,
} from '../interfaces/response-interface-history-sap';
import {
  OperazioneStoricoDashboard,
  IResponseStoricoDashboard,
} from '../interfaces/response-interface-history-dashboard';
import { IResponseRichiediExport } from '../interfaces/richiedi-export';
import { ResponseRetrieveRecipients } from '../interfaces/retrieve-recipients';

export interface IPersistenceTable<T> {
  columns?: IGridColumns[];
  result?: T;
  currentPage?: number;
}

@Injectable({
  providedIn: 'root',
})
export class PersistenceService {
  private persistenceState: Record<string, any> = {};

  private findDropdown(
    optionList: IDropdownOptions<any>[],
    value?: string
  ): IDropdownOptions<any> | undefined {
    if ([undefined, null].includes(value as any)) return undefined;

    return optionList.find((option: IDropdownOptions<any>) => {
      return option.payload === value || option.id === value;
    });
  }

  private formatDate(value: string): string | undefined {
    if (!value) return;

    const [day, month, year] = value.split('/');

    return `${year}-${month}-${day}`;
  }

  constructor() {}

  // Monitoring
  public setMonitoringFilters = (payload: IOperazioneFilter) => {
    let formattedPayload = {
      ...payload,
      dataDa: this.formatDate(payload.dataDa),
      dataA: this.formatDate(payload.dataA),
      operazione: this.findDropdown(
        operazioneDropdownOption,
        payload.operazione
      ),
      statoPartitaIva: this.findDropdown(
        generalStateDropdownOption,
        payload.statoPartitaIva
      ),
      statoDocumento: this.findDropdown(
        generalStateDropdownOption,
        payload.statoDocumento
      ),
      statoIndirizzo: this.findDropdown(
        generalStateDropdownOption,
        payload.statoIndirizzo
      ),
      statoMandato: this.findDropdown(
        generalStateDropdownOption,
        payload.statoMandato
      ),
      statoCfFirmatario: this.findDropdown(
        generalStateDropdownOption,
        payload.statoCfFirmatario
      ),
      statoCfRapprLegale: this.findDropdown(
        generalStateDropdownOption,
        payload.statoCfRapprLegale
      ),
    };

    this.persistenceState['monitoringFilters'] = formattedPayload;
  };

  public getMonitoringFilters = (): IOperazioneFilter | undefined => {
    return this.persistenceState['monitoringFilters'];
  };

  public setMonitoringList = (
    result: IPersistenceTable<IResponseOperationMonitoring>
  ) => {
    this.persistenceState['monitoringList'] = result;
  };

  public getMonitoringList = ():
    | IPersistenceTable<IResponseOperationMonitoring>
    | undefined => {
    return this.persistenceState['monitoringList'];
  };

  // History
  public setHistoryFilters = (payload: FilterStoricoOperazioni) => {
    let formattedPayload = {
      ...payload,
      dataDa: this.formatDate(payload.dataDa),
      dataA: this.formatDate(payload.dataA),
      operazione: this.findDropdown(
        operazioneDropdownOption,
        payload.operazione
      ),
      statoPartitaIva: this.findDropdown(
        generalStateDropdownOption,
        payload.statoPartitaIva
      ),
      statoDocumento: this.findDropdown(
        generalStateDropdownOption,
        payload.statoDocumento
      ),
      statoIndirizzo: this.findDropdown(
        generalStateDropdownOption,
        payload.statoIndirizzo
      ),
      statoMandato: this.findDropdown(
        generalStateDropdownOption,
        payload.statoMandato
      ),
      statoCfFirmatario: this.findDropdown(
        generalStateDropdownOption,
        payload.statoCfFirmatario
      ),
      statoCfRapprLegale: this.findDropdown(
        generalStateDropdownOption,
        payload.statoCfRapprLegale
      ),
    };

    this.persistenceState['historyFilters'] = formattedPayload;
  };

  public getHistoryFilters = (): FilterStoricoOperazioni | undefined => {
    return this.persistenceState['historyFilters'];
  };

  public setHistoryList = (
    result: IPersistenceTable<ResponseStoricoOperazioni>
  ) => {
    this.persistenceState['historyList'] = result;
  };

  public getHistoryList = ():
    | IPersistenceTable<ResponseStoricoOperazioni>
    | undefined => {
    return this.persistenceState['historyList'];
  };

  // Monitoring Interface
  public setMonitoringInterfaceFilters = (
    payload: OperazioneSAP | OperazioneCRM | OperazioneDashboard
  ) => {
    let formattedPayload = {
      ...payload,
      id: payload.idOperazione,
      vatNumber: payload.pIva,
      commercialOffice: payload.offertaCommerciale,
      intervalFrom: this.formatDate(payload.dataDa),
      intervalTo: this.formatDate(payload.dataA),
      system: this.findDropdown(systemOptions, payload.sistema),
      type: this.findDropdown(
        payload.sistema === 'crm' ? typeOptionsCrm : typeOptionsS4hana,
        payload.sistema === 'crm' ? payload.tipo : payload.tipoOperazioneSistema
      ),
      registryStatus: this.findDropdown(
        payload.sistema === 'crm'
          ? statusAnagCrmoOptions
          : statusAnagTypeS4HanaOptions,
        payload.statoAnag
      ),
      orderStatus: this.findDropdown(
        statusAccordoOptions,
        payload.statoAccordo
      ),
    };

    this.persistenceState['monitoringInterfaceFilters'] = formattedPayload;
  };

  // Monitoring Interface
  public setMonitoringInterfaceFiltersDashboard = (
    payload: OperazioneSAP | OperazioneCRM | OperazioneDashboard
  ) => {
    let typeDrodown : IDropdownOptions<any> | undefined;

    if(payload.sistema === 'dashboard'){
      typeDrodown = this.findDropdown(typeOptionsDashboard,payload.tipo);
    }else if(payload.sistema === 'crm'){
      typeDrodown = this.findDropdown(typeOptionsCrm,payload.tipo);
    }else{
      typeDrodown = this.findDropdown(typeOptionsS4hana,payload.tipoOperazioneSistema);
    }

    let formattedPayload = {
      ...payload,
      id: payload.idOperazione,
      vatNumber: payload.pIva,
      commercialOffice: payload.offertaCommerciale,
      intervalFrom: this.formatDate(payload.dataDa),
      intervalTo: this.formatDate(payload.dataA),
      system: this.findDropdown(systemOptionsLocker, payload.sistema),
      type: typeDrodown,
      registryStatus: this.findDropdown(
        payload.sistema === 'crm'
          ? statusAnagCrmoOptions
          : statusAnagTypeS4HanaOptions,
        payload.statoAnag
      ),
      orderStatus: this.findDropdown(
        statusAccordoOptions,
        payload.statoAccordo
      ),
    };

    this.persistenceState['monitoringInterfaceFilters'] = formattedPayload;
  };

  public getMonitoringInterfaceFilters = ():
    | OperazioneSAP
    | OperazioneCRM
    | OperazioneDashboard
    | undefined => {
    return this.persistenceState['monitoringInterfaceFilters'];
  };

  public setMonitoringInterfaceList = (
    result: IPersistenceTable<
      ResponseInterfaceMonitoringSap | ResponseInterfaceMonitoringCrm | ResponseInterfaceMonitoringDashboard
    >
  ) => {
    this.persistenceState['monitoringInterfaceList'] = result;
  };

  public getMonitoringInterfaceList = ():
    | IPersistenceTable<
        ResponseInterfaceMonitoringSap | ResponseInterfaceMonitoringCrm | ResponseInterfaceMonitoringDashboard
      >
    | undefined => {
    return this.persistenceState['monitoringInterfaceList'];
  };

  // History Interface
  public setHistoryInterfaceFilters = (
    payload: OperazioneStoricoSap | OperazioneStoricoCrm | OperazioneStoricoDashboard
  ) => {
    let formattedPayload = {
      ...payload,
      id: payload.idOperazione,
      partitaIva: payload.pIva,
      offCommerciale: payload.offertaCommerciale,
      dataCreazioneDa: this.formatDate(payload.dataDa),
      dataCreazioneA: this.formatDate(payload.dataA),
      sistema: this.findDropdown(systemOptions, payload.sistema),
      tipo: this.findDropdown(
        payload.sistema === 'crm' ? typeOptionsCrm : typeOptionsS4hana,
        payload.sistema === 'crm' ? payload.tipo : payload.tipoOperazioneSistema
      ),
      statoAnagrafica: this.findDropdown(
        payload.sistema === 'crm'
          ? statusAnagCrmoOptions
          : statusAnagTypeS4HanaOptions,
        payload.statoAnag
      ),
      statoOrdine: this.findDropdown(
        statusAccordoOptions,
        payload.statoAccordo
      ),
      stateOK: payload.stati.ok,
      stateKO: payload.stati.ko,
    };

    this.persistenceState['historyInterfaceFilters'] = formattedPayload;
  };

  public setHistoryInterfaceFiltersDashboard = (
    payload: OperazioneStoricoSap | OperazioneStoricoCrm | OperazioneStoricoDashboard
  ) => {
    let typeDrodown : IDropdownOptions<any> | undefined;

    if(payload.sistema === 'dashboard'){
      typeDrodown = this.findDropdown(typeOptionsDashboard,payload.tipo);
    }else if(payload.sistema === 'crm'){
      typeDrodown = this.findDropdown(typeOptionsCrm,payload.tipo);
    }else{
      typeDrodown = this.findDropdown(typeOptionsS4hana,payload.tipoOperazioneSistema);
    }

    let formattedPayload = {
      ...payload,
      id: payload.idOperazione,
      partitaIva: payload.pIva,
      offCommerciale: payload.offertaCommerciale,
      dataCreazioneDa: this.formatDate(payload.dataDa),
      dataCreazioneA: this.formatDate(payload.dataA),
      sistema: this.findDropdown(systemOptionsLocker, payload.sistema),
      tipo: typeDrodown,
      statoAnagrafica: this.findDropdown(
        payload.sistema === 'crm'
          ? statusAnagCrmoOptions
          : statusAnagTypeS4HanaOptions,
        payload.statoAnag
      ),
      statoOrdine: this.findDropdown(
        statusAccordoOptions,
        payload.statoAccordo
      ),
      stateOK: payload.stati.ok,
      stateKO: payload.stati.ko,
    };

    this.persistenceState['historyInterfaceFilters'] = formattedPayload;
  };


  public getHistoryInterfaceFilters = ():
    | OperazioneStoricoSap
    | OperazioneStoricoCrm
    | undefined => {
    return this.persistenceState['historyInterfaceFilters'];
  };

  public setHistoryInterfaceList = (
    result: IPersistenceTable<ResponseInterfaceHistoryCrm | IResponseStoricoSap | IResponseStoricoDashboard>
  ) => {
    this.persistenceState['historyInterfaceList'] = result;
  };

  public getHistoryInterfaceList = ():
    | IPersistenceTable<ResponseInterfaceHistoryCrm | IResponseStoricoSap | IResponseStoricoDashboard>
    | undefined => {
    return this.persistenceState['historyInterfaceList'];
  };

  //Destinatari
  public setDestinatariList = (
    result: IPersistenceTable<ResponseRetrieveRecipients>
  ) => {
    this.persistenceState['destinatariList'] = result;
  };

  public getDestinatariList =
    (): IPersistenceTable<ResponseRetrieveRecipients> => {
      return this.persistenceState['destinatariList'];
    };
}
